/** @format */

import {
	APP_BOOTSTRAP_LISTENER,
	APP_ID,
	ApplicationConfig,
	ErrorHandler,
	inject,
	provideAppInitializer
} from '@angular/core';
import { Chain, Environment, LocalStorageConfig } from '@shared/data-access/interfaces';
import {
	CompareEffects,
	ProductListEffects,
	compareReducer,
	productListReducers
} from '@plp/data-access/state';
import {
	CustomUrlSerializer,
	LoggerConfig,
	Store,
	TaggingService,
	WINDOW_PROVIDERS
} from '@shared/data-access/services';
import { DCSG_APP_NAME, GlobalErrorHandler } from '@logger';
import {
	UrlSerializer,
	provideRouter,
	withEnabledBlockingInitialNavigation
} from '@angular/router';
import { commonReducer, headerLocationReducer, homrReducer } from '@state/data-access';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideState, provideStore } from '@ngrx/store';

import { APP_BASE_HREF } from '@angular/common';
import { AUTH_URL_FILTER } from '@shared/authentication/data-access';
import { CriteoService } from '@shared/data-access/product-recommendations';
import { HTTP_INTERCEPTOR_PROVIDERS } from './app.interceptors';
import { PRODUCT_LIST_APP_ROUTES } from './app.routes';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { ProductListCompareService } from '@plp/data-access/services';
import { chain } from '@core/chain';
import { environment } from '../environments/environment';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';

const LOCAL_STORAGE_CONFIG: LocalStorageConfig = {
	prefix: ''
};
const LOGGER_CONFIG: LoggerConfig = {
	clientLoggerLevel: environment.logging.client.level,
	xhrLoggerLevel: environment.logging.xhr.level,
	xhrLoggerUrl: `${environment.api.spring.microServiceProxy.logger.path}publish`
};

/**
 * SRLP pages are still client-side rendered,
 * so we need to enable blocking navigation.
 */
const isSRLP = typeof window !== 'undefined' && window.location.pathname.startsWith('/search/');

export const appConfig: ApplicationConfig = {
	providers: [
		Store,
		provideStore({
			common: commonReducer,
			homr: homrReducer,
			headerLocation: headerLocationReducer,
			router: routerReducer
		}),
		provideEffects([ProductListEffects, CompareEffects]),
		provideState('productList', {
			productListCore: productListReducers
		}),
		provideState('compare', compareReducer),
		provideRouter(
			PRODUCT_LIST_APP_ROUTES,
			...(isSRLP ? [withEnabledBlockingInitialNavigation()] : [])
		),
		provideRouterStore(),
		provideStoreDevtools({
			maxAge: environment.production ? 5 : 25,
			logOnly: environment.production,
			connectInZone: true
		}),
		{ provide: LoggerConfig, useValue: LOGGER_CONFIG },
		provideHttpClient(withFetch(), withInterceptorsFromDi()),
		{ provide: Environment, useValue: environment },
		{ provide: APP_ID, useValue: 'dcsg-ngx-plp-server' },
		{ provide: APP_BASE_HREF, useValue: '/' },
		{ provide: LocalStorageConfig, useValue: LOCAL_STORAGE_CONFIG },

		{
			provide: AUTH_URL_FILTER,
			useValue: [
				environment.baseUrls.pointService,
				environment.baseUrls.identityService,
				environment.baseUrls.athleteService,
				environment.api.myLocker
			]
		},
		/*
		 * APP_INITIALIZER InjectionToken is used to perform any logic during app initialization,
		 * this would execute before AppComponent or any Resolve Guards.  This guarantees anything
		 * required for setup is completed ahead of time.
		 */
		provideAppInitializer(() => {
			const initializerFn = (
				(service: ProductAnalyticsService) => () =>
					service.resetAnalytics()
			)(inject(ProductAnalyticsService));
			return initializerFn();
		}),
		{
			provide: APP_BOOTSTRAP_LISTENER,
			useFactory: (service: ProductListCompareService) => () =>
				service.readyToInit.next(true),
			deps: [ProductListCompareService],
			multi: true
		},
		WINDOW_PROVIDERS,
		{ provide: Chain, useValue: chain || {} },
		{ provide: DCSG_APP_NAME, useValue: 'PLP' },
		{ provide: ErrorHandler, useClass: GlobalErrorHandler },
		...HTTP_INTERCEPTOR_PROVIDERS,
		{ provide: UrlSerializer, useClass: CustomUrlSerializer },
		TaggingService,
		provideClientHydration(withEventReplay()),
		CriteoService,
		provideAnimationsAsync()
	]
};
